import { RtkQueryAdapter } from '../infra/adapters/rtk.adapter';
import { createApiSliceService } from '../application/services/apiSliceService';
import type { QueryClient, QueryConfig } from '../domain/interfaces/QueryClient';
import type { ApiSliceService, ApiSliceConfig } from '../application/services/apiSliceService';
// import { FetchQueryClientAdapter } from '../infra/adapters/fetch.adapter.example'; // Commented - using RTK Query

/**
 * Query Feature Builder
 * ---------------------
 * Factory function to wire infrastructure -> application layer
 * This is where dependency injection happens
 * 
 * Wires:
 * - Query client adapter (infrastructure)
 * - API slice service (application)
 * 
 * Can work with any query library adapter (RTK Query, TanStack Query, etc.)
 * To swap adapters, change the adapter import and factory function
 */

/**
 * Create query client factory (infrastructure layer)
 * Chooses which adapter implementation to use
 * 
 * @param config - Query configuration
 * @returns QueryClient interface implementation
 */
function createQueryClient(config: QueryConfig): QueryClient {
  // Currently using RTK Query adapter
  // To test Fetch adapter: return new FetchQueryClientAdapter(config);
  
  return new RtkQueryAdapter(config);
  
  // Fetch adapter (commented - using RTK Query)
  // return new FetchQueryClientAdapter(config);
}

/**
 * Create API slice service (application layer)
 * Wires infrastructure adapter with application service
 * 
 * @returns API slice service with createApiSlice method
 */
export function queryBuild(): { service: ApiSliceService } {
  const service = createApiSliceService(createQueryClient);
  return { service };
}

/**
 * Create API slice with simplified configuration
 * Convenience function that uses the wired service
 * 
 * @param config - API slice configuration with endpoints
 * @returns API slice instance with hooks
 */
export function createApiSlice(config: ApiSliceConfig) {
  const { service } = queryBuild();
  return service.createApiSlice(config);
}

